import Control from '@/Controls/Control';
import ComponentManager from '@/Management/ComponentManager';
import DropDownListViewModelType from '@/ViewModels/Controls/DropDownListViewModel';
import KendoDropDownHelper from '@/Utility/KendoDropDownHelper';
import Utilities from '@/Utility/Utilities';

export default class DropDownList extends Control {

    private jQueryControl: JQuery;

    /**
     * @override
     */
    public widget: KnockoutObservable<kendo.ui.DropDownListExt>;

    public constructor(private viewModel: DropDownListViewModelType, private element?: JQuery) {
        super(viewModel, element);

        this.onWidgetCreated(this.onWidgetCreatedCallback.bind(this));
    }

    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.jQueryControl = this.element || $('<div><select></div>');

        let $select = this.jQueryControl.find('select');
        if ($select.length == 0) {
            $select = ($('<select>') as JQuery<HTMLSelectElement>).appendTo(this.jQueryControl);
        }

        this.applyBindings($select, {
            kendoDropDownList: {
                data: this.viewModel.data,
                value: this.viewModel.value,
                enabled: this.viewModel.isEnabled,
                change: this.viewModel['onChangeInternal'].bind(this.viewModel),
                select: this.onSelect.bind(this),
                close: this.viewModel.onClose.bind(this.viewModel),
                open: this.onOpen.bind(this),

                dataTextField: this.viewModel.dataTextField,
                dataValueField: this.viewModel.dataValueField,

                template: this.viewModel.template,
                valueTemplate: this.viewModel.valueTemplate,

                animation: this.viewModel.animation,
                height: this.viewModel.height,
                optionLabel: this.viewModel.optionLabel,
                widget: this.widget
            }
        });

        return this.jQueryControl;
    }

    private onWidgetCreatedCallback(widget: kendo.ui.DropDownListExt): void {
        widget.bind('dataBound', () => {
            ko.ignoreDependencies(() => { // Diese Funktion wird in knockout-kendo in einem computed ausgeführt, etwaige Abhängigkeiten wollen wir nicht registrieren
                let vmVal = this.viewModel.value();
                if (vmVal == null) {
                    vmVal = "";
                }

                // Die DropdownList hat einen Bug:
                // Aktualisiert man die Liste der Werte, so wird der angezeigte aktuelle Wert nicht aktualisiert;
                // intern wird bei Abfragen des aktuellen Wertes aber schon die erste verfügbare <option> aus dem Popup verwendet um den Wert zu bestimmen.
                //
                // Wird dann ein Wert gesetzt, so findet folglich kein Update in der Anzeige statt, wenn das erste der Werte aus der Liste
                // mit dem zu setzenden Wert übereinstimmt.
                //
                // Mit dem folgenden Code wird sichergestellt, dass die Anzeige aktualisiert wird.

                if (vmVal != "") {
                    widget.value("");
                }
                widget.value(vmVal);
            });
        });

        if (ko.isObservable(this.viewModel.optionLabel)) {
            // Falls Veränderungen am Option-Label stattfinden, sollen diese auch angezeigt werden
            this.defineSubscription(this.viewModel.optionLabel, (optionLabel) => {
                widget.options.optionLabel = optionLabel;
                widget.setOptions(widget.options);
            });
        }

        this.applyBindings(this.jQueryControl, {
            controlName: 'dropdownlist',
            technicalName: this.viewModel.technicalName,
            loader: this.viewModel.isBusy,
            controlIdent: this.identifier,
            tooltip: this.viewModel.tooltip,
            visible: this.viewModel.isVisible,
            invalid: this.viewModel.invalidationText,
            interact: this.viewModel.isEnabled,
            styles: this.definePureComputed({
                read: () => {
                    let styles = this.viewModel.styles();

                    if (this.viewModel.isToolBarLink() === true) {
                        styles.push('toolbar');
                    }

                    if (this.viewModel.isAutoWidth() === true) {
                        styles.push('autowidth');
                    }

                    if (styles.length == 0) {
                        styles.push('default');
                    }

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];
                    if (this.viewModel.isRequired() === true) states.push('required');
                    if (this.viewModel.isValid() === true) states.push('valid');
                    if (this.viewModel.isValid() !== true) states.push('invalid');
                    if (this.viewModel.isEnabled() === true) states.push('enabled');
                    if (this.viewModel.isEnabled() !== true) states.push('disabled');

                    return states;
                }
            }),
            css: this.defineComputed(() => {
                return {
                    open: this.viewModel.isOpen()
                }
            })
        });

        this.applyBindings(widget.list, {
            technicalName: this.viewModel.technicalName
        });

        this.applyBindings(widget.wrapper, {
            focus: this.viewModel.isFocusable
        });

        // Kendo setzt fälschlicherweise role="listbox" auf der Wrapper; combobox ist semantisch
        // korrekt und erlaubt Kindelemente wie den k-select-Pfeil-Span (WCAG 1.3.1).
        widget.wrapper.attr('role', 'combobox');

        // Fallback-Name für den Fall, dass es kein sichtbares Label gibt. Existiert ein sichtbares
        // Label, hebt addLabelReference() dieses via aria-labelledby zur alleinigen Namensquelle
        // und entfernt dieses aria-label wieder (WCAG 4.1.2).
        if (this.viewModel.ariaLabel) {
            widget.wrapper.attr('aria-label', this.viewModel.ariaLabel);
        }

        KendoDropDownHelper.bindWidgetLocators(widget, this.viewModel.locator, this.viewModel.itemLocatorStrategy);

        this.applyBindings(widget.ul.closest('.k-list-container'), {
            controlName: Utilities.isBlank(this.viewModel.listContainerName) 
                ? 'dropdownlist-container' 
                : this.viewModel.listContainerName,

            styles: this.definePureComputed({
                read: () => {
                    let styles = this.viewModel.styles();

                    if (this.viewModel.isToolBarLink() === true) {
                        styles.push('toolbar');
                    }

                    if (this.viewModel.isAutoWidth() === true) {
                        styles.push('autowidth');
                    }

                    if (styles.length == 0) {
                        styles.push('default');
                    }

                    return styles;
                }
            }),
            states: this.definePureComputed({
                read: () => {
                    let states = [];
                    if (this.viewModel.isRequired() === true) states.push('required');
                    if (this.viewModel.isValid() === true) states.push('valid');
                    if (this.viewModel.isValid() !== true) states.push('invalid');
                    if (this.viewModel.isEnabled() === true) states.push('enabled');
                    if (this.viewModel.isEnabled() !== true) states.push('disabled');

                    return states;
                }
            })
        });

        /**
         * This overwriting is necessary because the data must first be loaded when you click. We do not use Kendo's LazyLoading, but implement it ourselves via the data source.
         * The DropDown would not open if this loading was not done beforehand, because the data is empty.
         */
        widget.wrapper.off('click.kendoDropDownList');
        this.addEventHandler('click', widget.wrapper, () => {
            if (!this.viewModel.isEnabled())
                return;
            if (!this.viewModel.isOpen()) {
                this.evaluate(this.viewModel.onOpen()).then(() => {
                    this.async(() => {
                            widget.focus();
                            widget.open();
                        },
                        1);
                    // Das Kendo-Knockout-Binding verwendet einen Timeout von 1ms; d.h. die Daten sind nun noch gar nicht beim Widget angekommen.
                    // Im FF kam es dadurch zu Darstellungsfehlern beim initialen Öffnen dieser asynchron geladenen Daten, wenn man das Widget hier mit einem 0ms Timeout geöffnet hat.
                    // Der Wechsel auf 1ms hat das Problem behoben.   }
                });
            } else {
                this.evaluate(this.viewModel.onClose()).then(() => {
                    this.async(() => {
                            widget.focus();
                            widget.close();
                        },
                        1);
                });
            }
        });

        this.defineSubscription(this.viewModel.data, () => {
            if (this.viewModel.isOpen())
                this.resetDropDownPopupPosition()
        });
        
        this.addEventHandler("touchend",
            widget.list,
            (event: Event) => { $(event.target).click() });
    }

    private onOpen(e: kendo.ViewEvent) {
        if (!this.viewModel.isEnabled()) {
            e?.preventDefault();
            return;
        }

        this.viewModel.onOpen().then(this.resetDropDownPopupPosition.bind(this));
    }

    private resetDropDownPopupPosition(): void {
        let labels = this.getAllListLabels();
        
        KendoDropDownHelper.resetDropDownPopupPosition(labels, this.jQueryControl, this.widget());
    }

    private getAllListLabels(): string[] {
        // Template is used -> template must be rendered
        if (Utilities.isFunction(this.widget()?.listView?.templates?.template)) {
            return this.viewModel.data().map((value) => {
                return this.getTemplatedListEntryLabel(value);
            });
        } else {
            return this.viewModel.data().map((value: string) => {
                return value[this.viewModel.dataTextField];
            });
        }
    }

    private getTemplatedListEntryLabel(entry) {
        let template = this.widget().listView.templates.template(entry);
        if (template.trim().startsWith('<')) {
            try {
                let selector = $(template);
                if (selector.length == 0)
                    return template;

                // Es können führende und angehängte Umbrüche/Leerzeichen enthalten sein, je nach HTML.
                // Uns interessiert jedoch nur der Text-Inhalt.
                return selector.text().replace(/^[\r\n\s]*(.*?)[\r\n\s]*$/, "$1");
            } catch (e) {

            }
        }
        return template;
    }

    /**
     * Internal callback for selecting values
     * @param event
     */
    private onSelect(event) {
        this.viewModel.onSelectInternal(event);
        /**
         * SAGGRC-628
         * https://docs.telerik.com/kendo-ui/api/javascript/ui/dropdownlist/methods/select
         * If a value is selected, this should also automatically trigger a change
         * Since there is only one hook before the selection and not after the selection, the change event must be triggered asynchronously
         */
        // TODO: Needs review
        // this.async(() => this.widget().trigger('change'), 1);
    }

    public addLabelReference(label: JQuery): JQuery {
        // Native <label for> Verknüpfung mit dem versteckten <select>
        let id = this.identifier + '-select';
        this.getControl().find('select').attr('id', id);
        label.attr('for', id);

        // <label for> benennt nur das versteckte <select>, nicht die combobox-Wrapper.
        // Daher wird die Wrapper über aria-labelledby mit dem Label verknüpft. Ein evtl.
        // gesetztes Fallback-aria-label wird entfernt, damit es nur eine Namensquelle gibt.
        const labelId = this.identifier + '-label';
        label.attr('id', labelId);

        const wrapper = this.widget()?.wrapper;
        if (wrapper) {
            wrapper.attr('aria-labelledby', labelId)
                   .removeAttr('aria-label');
        }

        return label;
    }
}

ComponentManager.setControl('DropDownList', DropDownList, 'RADPresentation.HTMLClient.DefaultViewExtensions');